import type { ClosedPeriodOverride } from '../../app/closed-period-dialog.js';
import type { ReconciliationRuleOperator } from '@hue/contracts';
import { api } from '../../lib/api.js';

export interface AccountingAccount {
  id: string;
  code: string;
  name: string;
  active: boolean;
  /// Quantas partes, compromissos e recorrências apontam a conta. Conta em uso não ganha subconta.
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export type QueueKind = 'MOVEMENTS' | 'OBLIGATIONS' | 'RECURRENCES';
export type SubjectKind = 'MOVEMENT' | 'DIFFERENCE' | 'OBLIGATION' | 'RECURRENCE';
export type QueuePart = 'WAITING' | 'DECLARED' | 'DIFFERENCE' | 'OBLIGATION' | 'RECURRENCE';

export interface QueueAccount {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

export interface QueueRow {
  subjectKind: SubjectKind;
  subjectKey: string;
  part: QueuePart;
  date: string | null;
  description: string;
  detail: string;
  direction: 'INFLOW' | 'OUTFLOW';
  amountCents: number | null;
  movementCents: number | null;
  reason: string | null;
  linked: boolean;
  competenceMonth: string | null;
  account: QueueAccount | null;
  origin: 'MANUAL' | 'RULE' | 'FORM' | 'RECURRENCE' | null;
  ruleName?: string | null;
  ruleRemoved?: boolean;
  /// Quando a regra classificou: em Regras (aplicar ou reclassificar) ou sozinha, na importação ou
  /// ao desconciliar.
  ruleTrigger?: 'APPLY' | 'RECLASSIFY' | 'IMPORT' | 'UNRECONCILE' | null;
  /// A regra que casa com esta parte hoje, para explicar por que ficou como está.
  ruleHint?: { name: string; account: { code: string; name: string }; suspended: boolean } | null;
  classifiedAt: string | null;
  suggestion: QueueAccount | null;
  /// As parcelas da mesma compra, quando a linha junta várias. Cada uma segue sendo um compromisso.
  parcels: QueueRow[] | null;
  /// Parcelas com contas diferentes, ou umas com conta e outras sem.
  mixed: boolean;
}

export interface QueuePage {
  rows: QueueRow[];
  total: number;
  summary: {
    pendingCount: number;
    pendingCents: number;
    waitingCents: number;
    classifiedRatio: number | null;
  };
}

export interface QueueParams {
  kind: QueueKind;
  from: string;
  to: string;
  filter: 'PENDING' | 'CLASSIFIED' | 'ALL';
  search?: string | undefined;
  sort: 'date' | 'description' | 'amount';
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
}

export const accountingApi = {
  accounts: async () =>
    (await api.get<{ accounts: AccountingAccount[] }>('/tenant/accounting/accounts')).data.accounts,
  adoptSuggested: async () =>
    (await api.post<{ accounts: AccountingAccount[] }>('/tenant/accounting/accounts/suggested'))
      .data.accounts,
  create: async (input: { code: string; name: string }) =>
    (await api.post<{ account: AccountingAccount }>('/tenant/accounting/accounts', input)).data
      .account,
  rename: async (id: string, name: string) =>
    (await api.patch<{ account: AccountingAccount }>(`/tenant/accounting/accounts/${id}`, { name }))
      .data.account,
  setActive: async (id: string, active: boolean) =>
    (
      await api.put<{ account: AccountingAccount }>(
        `/tenant/accounting/accounts/${id}/activation`,
        { active },
      )
    ).data.account,
  queue: async (params: QueueParams) =>
    (await api.get<QueuePage>('/tenant/accounting/queue', { params })).data,
  /// O pedido de ajuste só vai quando a pessoa escolheu "Salvar e lançar ajuste" num mês fechado.
  classify: async (
    targets: { kind: SubjectKind; key: string }[],
    accountId: string,
    closedPeriodAdjustment?: ClosedPeriodOverride,
  ) =>
    (
      await api.post<{ count: number }>('/tenant/accounting/classifications', {
        targets,
        accountId,
        ...(closedPeriodAdjustment ? { closedPeriodAdjustment } : {}),
      })
    ).data.count,
  clear: async (
    targets: { kind: SubjectKind; key: string }[],
    closedPeriodAdjustment?: ClosedPeriodOverride,
  ) =>
    (
      await api.post<{ count: number }>('/tenant/accounting/classifications/clear', {
        targets,
        ...(closedPeriodAdjustment ? { closedPeriodAdjustment } : {}),
      })
    ).data.count,
  setCompetence: async (
    kind: SubjectKind,
    key: string,
    competenceMonth: string,
    closedPeriodAdjustment?: ClosedPeriodOverride,
  ) => {
    await api.put('/tenant/accounting/classifications/competence', {
      kind,
      key,
      competenceMonth,
      ...(closedPeriodAdjustment ? { closedPeriodAdjustment } : {}),
    });
  },
  declare: async (
    input: {
      movementId: string;
      reason: string;
      competenceMonth: string;
      accountId: string | null;
    },
    closedPeriodAdjustment?: ClosedPeriodOverride,
  ) => {
    await api.post('/tenant/accounting/declarations', {
      ...input,
      ...(closedPeriodAdjustment ? { closedPeriodAdjustment } : {}),
    });
  },
  /// Desfazer a declaração é do Financeiro: a conta e a competência ficam guardadas aqui e voltam
  /// a valer se o movimento for declarado de novo.
  undeclare: async (movementId: string) => {
    await api.delete(`/tenant/reconciliation/movements/${movementId}/dismissal`);
  },
};

export interface ClassificationRuleInput {
  name: string;
  mode: 'SIMPLE' | 'REGEX';
  operator?: ReconciliationRuleOperator | null;
  terms?: string[];
  pattern?: string;
  direction: 'INFLOW' | 'OUTFLOW' | null;
  minAmountCents: number | null;
  maxAmountCents: number | null;
  accountId: string;
}
export interface ClassificationRule extends ClassificationRuleInput {
  id: string;
  priority: number;
  pattern: string;
  terms: string[];
  account: { id: string; code: string; name: string };
  suspended: boolean;
  applyCount: number;
  reclassifyCount: number;
}
export interface RuleCandidate {
  kind: SubjectKind;
  key: string;
  description: string;
  direction: 'INFLOW' | 'OUTFLOW';
  amountCents: number;
  financeAccountId: string;
  date: string;
  accountId: string | null;
  origin: QueueRow['origin'];
  ruleId: string | null;
}
export type RuleAction = 'apply' | 'reclassify';
export interface RulePreview {
  count: number;
  /// Partes que a regra pegaria, mas estão em mês fechado: ficam como estão (ADR 0064).
  skippedClosed: number;
  totalCents: number;
  sample: Array<Pick<RuleCandidate, 'description' | 'amountCents' | 'date' | 'accountId'>>;
}
export const classificationRulesApi = {
  list: async () =>
    (await api.get<{ rules: ClassificationRule[] }>('/tenant/accounting/rules')).data.rules,
  candidates: async () =>
    (await api.get<{ candidates: RuleCandidate[] }>('/tenant/accounting/rules/candidates')).data
      .candidates,
  create: async (input: ClassificationRuleInput) =>
    (await api.post<{ rule: ClassificationRule }>('/tenant/accounting/rules', input)).data,
  update: async (id: string, input: ClassificationRuleInput) =>
    (await api.patch<{ rule: ClassificationRule }>(`/tenant/accounting/rules/${id}`, input)).data,
  remove: async (id: string) => {
    await api.delete(`/tenant/accounting/rules/${id}`);
  },
  reorder: async (ruleIds: string[]) => {
    await api.put('/tenant/accounting/rules/order', { ruleIds });
  },
  preview: async (id: string, action: RuleAction) =>
    (await api.get<RulePreview>(`/tenant/accounting/rules/${id}/preview`, { params: { action } }))
      .data,
  apply: async (id: string, action: RuleAction) =>
    (
      await api.post<{ count: number; skippedClosed: number }>(
        `/tenant/accounting/rules/${id}/application`,
        { action },
      )
    ).data,
};

export type RevenueNature = 'SERVICE' | 'PRODUCT' | 'RETURN' | 'RETENTION';
export interface RevenueAccount {
  nature: RevenueNature;
  account: QueueAccount | null;
}
export const revenueAccountsApi = {
  list: async () =>
    (await api.get<{ revenueAccounts: RevenueAccount[] }>('/tenant/accounting/revenue-accounts'))
      .data.revenueAccounts,
  set: async (nature: RevenueNature, accountId: string | null) =>
    (
      await api.put<{ revenueAccounts: RevenueAccount[] }>(
        `/tenant/accounting/revenue-accounts/${nature}`,
        { accountId },
      )
    ).data.revenueAccounts,
};
